import React, { useLayoutEffect, useMemo, memo } from "react";
import { isArray } from "lodash";

import { ChatMessage } from "../../../services";
import { useTypedSelector } from "../../../redux/store";
import { useModelSubscribe } from "../../../hooks";
import { useChatActions } from "../../OrderPageWidgets/Chat/hooks";
import { useFilteredExecutors } from "../../OrderPageWidgets/hook";

const ChatMessageLoader: React.FC<ChatMessageLoader.Props> = () => {
	const { connected, authorized } = useTypedSelector((state) => state.prpc);
	const executors = useTypedSelector(
		(state) => state.globalState.executors.data.subscription.basic,
	);

	const { filterExecutorsRead } = useTypedSelector(
		(state) => state.orders.chats.filter,
	);

	const lang = useTypedSelector((state) => state.session.language);

	const canSubscribe = useMemo(
		() => connected && authorized,
		[authorized, connected],
	);

	const { userModalData, lastRead } = useTypedSelector(
		(state) => state.orders.chats.chat,
	);

	const { onSaveLastReadFirstLoading, onSaveLastReadRemaining, setModels } =
		useChatActions();

	const filteredExecutors = useFilteredExecutors(
		userModalData.executors,
		filterExecutorsRead.criteria,
		{
			companyIds: true,
			taxiServiceIds: true,
			executorIds: true,
			executorGroupIds: true,
			fleetIds: true,
		},
	);

	const showedExecutorIds = useMemo(() => {
		if (!filteredExecutors.length) {
			return undefined;
		}
		const retval = filteredExecutors?.map((executor) => executor.id);
		return retval;
	}, [filteredExecutors]);

	const modelSubscriptionOptions =
		useMemo<ChatMessage.SubscribeOptions>(() => {
			const isTurnOnFiltrationExecutorMessage =
				isArray(showedExecutorIds);

			const result: ChatMessage.SubscribeOptions = {
				order: { createdAt: "ASC" },
				lang,
				...(isTurnOnFiltrationExecutorMessage
					? { showedExecutorIds }
					: {}),
			};

			return result;
		}, [showedExecutorIds, lang]);

	const chatMessageSubscribe = useModelSubscribe(
		modelSubscriptionOptions,
		ChatMessage,
	);

	const listServices = useMemo(() => {
		const models = chatMessageSubscribe?.models || [];
		const loading =
			typeof chatMessageSubscribe?.loading === "boolean"
				? chatMessageSubscribe.loading
				: true;

		// const dataAfterAccess = subTaxiServiceFilterAccess({
		// 	models,
		// 	branchPath: "taxiService.id",
		// });

		const idsExecutorsAccess = new Set(
			executors.map((executor) => executor.id),
		);

		const modelsAccess = models.filter((model) => {
			const isDispatcher = model.dispatcher;
			const isExecutorToIds = idsExecutorsAccess.has(
				model.executor?.id ?? -1,
			);
			const executorFind = executors.find(
				(executor) => executor.id === (model.executor?.id ?? -1),
			);
			const executorFindToTaxiServerId =
				executorFind?.taxiService?.id || null;

			const taxiServiceIdsToFilter = (
				filterExecutorsRead.criteria.taxiServiceIds?.[0] === "all"
					? null
					: filterExecutorsRead.criteria.taxiServiceIds
			) as number[] | null;

			const isExecutorToTaxiServer =
				taxiServiceIdsToFilter && executorFindToTaxiServerId
					? taxiServiceIdsToFilter.includes(
							executorFindToTaxiServerId,
					  )
					: true;

			const isExecutor = isExecutorToIds && isExecutorToTaxiServer;

			const retval = isDispatcher || isExecutor;

			return retval;
		});

		return { models: modelsAccess, loading };
	}, [
		chatMessageSubscribe?.loading,
		chatMessageSubscribe?.models,
		executors,
		filterExecutorsRead.criteria.taxiServiceIds,
	]);

	useLayoutEffect(() => {
		if (!canSubscribe) return;

		const { models, loading } = listServices;
		// * Dynamic recording of unread messages
		const unreadMessages =
			models?.filter((message) => message.id > lastRead.newId) || [];

		onSaveLastReadRemaining(unreadMessages.length);
		setModels(models, loading);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [listServices, canSubscribe, lastRead.newId]);

	useLayoutEffect(() => {
		if (!canSubscribe) return;

		const fetchLastRead = async () => {
			try {
				const response = await ChatMessage.getLastRead();

				if (response && response.chatMessageId) {
					const { chatMessageId } = response;
					if (chatMessageId > 0)
						onSaveLastReadFirstLoading(chatMessageId as number);
				}
			} catch (error) {
				console.error("[chat]: Error fetching last read:", error);
			}
		};

		fetchLastRead();
	}, [onSaveLastReadFirstLoading, canSubscribe]);

	return null;
};

declare namespace ChatMessageLoader {
	interface Props {}
}

export default memo(ChatMessageLoader);
